/**
 * A person's change to a version's scan status: what this viewer is shown, and may do.
 *
 * The rules live on the server (backend/src/lib/scan-status-change.js) and the server enforces
 * them. This only decides which controls to offer, so nobody is offered one that will be refused -
 * above all, the requester is never offered Approve on their own request.
 */

export type ScanStatusChangeState = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'revoked'

export interface ScanStatusChangePerson { id: number; name: string | null }

export interface ScanStatusChange {
    id: number
    versionId: number
    objectId: string
    objectName: string
    previousStatus: number
    newStatus: number
    scanId: string | null
    reason: string
    state: ScanStatusChangeState
    requestedBy: ScanStatusChangePerson | null
    requestedAt: string
    decidedBy: ScanStatusChangePerson | null
    decidedAt: string | null
    decisionNote: string | null
    revokedBy: ScanStatusChangePerson | null
    revokedAt: string | null
    revokeReason: string | null
    /** The requester ticked "also accept this scan as the new reference". */
    acceptReference?: boolean
    /** null until approved; then what became of the acceptance. */
    referenceState?: 'queued' | 'accepted' | 'nothing' | 'failed' | null
    referenceDetail?: string | null
    referenceDoneAt?: string | null
}

/** The statuses a person may change FROM: a scan result - red, amber, green, indicators. Mirrors the server. */
export const CHANGEABLE_FROM: ReadonlySet<number> = new Set([2, 3, 4, 5])
/** The statuses a person may change TO, most cautious first: red, amber, green. */
export const CHANGEABLE_TO: readonly number[] = [2, 3, 4]
export const MIN_REASON_LENGTH = 20

export interface ScanStatusChangeView {
    /** An approved change that is the version's status right now. */
    inEffect: ScanStatusChange | null
    /** A request waiting for a second person. */
    pending: ScanStatusChange | null
    /** The newest approved change, when a later scan has replaced it. */
    superseded: ScanStatusChange | null
    canRequest: boolean
    /** The statuses that may be asked for: red, amber and green, less the one in effect. */
    targets: number[]
    canApprove: boolean
    canReject: boolean
    canWithdraw: boolean
    canRevoke: boolean
    /** This viewer asked for the pending change, so cannot approve it. */
    isOwnRequest: boolean
    /**
     * The pending request was made against a status the version no longer has: it has been scanned
     * again since (or changed some other way). The server will not approve it; say so, and offer
     * only the ways to close it.
     */
    pendingOutOfDate: boolean
    /** Every change, newest first. */
    history: ScanStatusChange[]
}

export function scanStatusChangeView(input: {
    /** The version's effective status. */
    status: number | null | undefined
    /** 'override' when a person's change is the status in effect. */
    analyticsSource: string | null | undefined
    changes: ScanStatusChange[] | null | undefined
    myUserId: number | null | undefined
    mayRequest: boolean
    mayApprove: boolean
}): ScanStatusChangeView {
    const history = [...(input.changes || [])].sort((a, b) => b.id - a.id)
    const overridden = input.analyticsSource === 'override'
    const newestApproved = history.find((c) => c.state === 'approved') || null
    const inEffect = overridden ? newestApproved : null
    const superseded = !overridden ? newestApproved : null
    const pending = history.find((c) => c.state === 'pending') || null
    const isOwnRequest = !!pending && pending.requestedBy?.id != null && Number(pending.requestedBy.id) === Number(input.myUserId)

    // A version already changed can be changed again (green by review, then red when more is
    // known): approving the new change retires the old one.
    const canRequest = input.mayRequest && !pending && CHANGEABLE_FROM.has(Number(input.status))
    // Andrew, 6 Oct: a request "from red to green" sat on a version that a later scan had already
    // turned green, still reading "waiting for a second person" as if approving it meant something.
    const pendingOutOfDate = !!pending && input.status != null && Number(pending.previousStatus) !== Number(input.status)
    return {
        targets: canRequest ? CHANGEABLE_TO.filter((s) => s !== Number(input.status)) : [],
        inEffect,
        pending,
        superseded,
        history,
        isOwnRequest,
        canRequest,
        // Two people: never the requester. Not knowing who is looking is not permission either.
        pendingOutOfDate,
        canApprove: !!pending && !pendingOutOfDate && input.mayApprove && input.myUserId != null && !isOwnRequest,
        canReject: !!pending && input.mayApprove && !isOwnRequest,
        canWithdraw: isOwnRequest,
        canRevoke: !!inEffect && input.mayApprove,
    }
}
